import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, FilePenLine, LoaderCircle } from "lucide-react";
import { toast } from "sonner";
import LegalTextEditor from "@/components/contracts/LegalTextEditor";
import { centsInput, contractSettingsApi, latestEditableDraft, publishedVersion } from "@/lib/contractSettings";

const tabs = [
  { id: "tariffs", label: "Tarife" },
  { id: "usage_contract", label: "Nutzungsvertrag" },
  { id: "withdrawal_policy", label: "Widerruf" },
  { id: "privacy_notice", label: "Datenschutz-Hinweis" },
];

const emptyTitles = {
  usage_contract: "Nutzungsvertrag – Feuerwehr Kantine",
  withdrawal_policy: "Widerrufsbelehrung",
  privacy_notice: "Datenschutz-Hinweis",
};

export default function ContractSettings() {
  const [activeTab, setActiveTab] = useState("tariffs");
  const [templates, setTemplates] = useState([]);
  const [tariffs, setTariffs] = useState({ single: { 6: "24,00", 12: "48,00" }, community: { 6: "21,00", 12: "42,00" } });
  const [editor, setEditor] = useState({ id: null, title: "", html: "", sanitizedPreview: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [templateResponse, tariffResponse] = await Promise.all([
        contractSettingsApi.getTemplates(),
        contractSettingsApi.getTariffs(),
      ]);
      setTemplates(templateResponse.data || []);
      const configured = tariffResponse.data?.contract_tariffs || {};
      const euro = (value) => ((Number(value || 0) / 100).toFixed(2).replace(".", ","));
      setTariffs({
        single: { 6: euro(configured.single?.[6] ?? configured.single?.["6"] ?? 2400), 12: euro(configured.single?.[12] ?? configured.single?.["12"] ?? 4800) },
        community: { 6: euro(configured.community?.[6] ?? configured.community?.["6"] ?? 2100), 12: euro(configured.community?.[12] ?? configured.community?.["12"] ?? 4200) },
      });
    } catch (error) { toast.error(error.response?.data?.detail || "Vertragseinstellungen konnten nicht geladen werden"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const versions = useMemo(() => templates.filter((item) => item.type === activeTab), [templates, activeTab]);
  const published = publishedVersion(versions);
  const draft = latestEditableDraft(versions);

  useEffect(() => {
    if (activeTab === "tariffs") return;
    const source = draft || published;
    setEditor({
      id: draft?.id || null,
      title: source?.title || emptyTitles[activeTab],
      html: source?.html || "<h2>Hinweis</h2><p>Text hier eintragen.</p>",
      sanitizedPreview: source?.html || "",
    });
  }, [activeTab, draft?.id, published?.id]);

  const saveDraft = async () => {
    setSaving(true);
    try {
      const response = editor.id
        ? await contractSettingsApi.updateTemplate(editor.id, { title: editor.title, html: editor.html })
        : await contractSettingsApi.createTemplate({ type: activeTab, title: editor.title, html: editor.html });
      setEditor((current) => ({ ...current, id: response.data.id, html: response.data.html, sanitizedPreview: response.data.html }));
      toast.success("Entwurf serverseitig bereinigt und gespeichert");
      const refreshed = await contractSettingsApi.getTemplates();
      setTemplates(refreshed.data || []);
    } catch (error) { toast.error(error.response?.data?.detail || "Entwurf konnte nicht gespeichert werden"); }
    finally { setSaving(false); }
  };

  const publish = async () => {
    if (!editor.id || !window.confirm("Diese Fassung veröffentlichen? Sie gilt für zukünftige Neuverträge und zustimmungspflichtige Änderungen. Bestehende Vertragssnapshots bleiben unverändert.")) return;
    try {
      const { data } = await contractSettingsApi.publishTemplate(editor.id);
      toast.success(`Version ${data.version} veröffentlicht`);
      setEditor((current) => ({ ...current, id: null }));
      const refreshed = await contractSettingsApi.getTemplates();
      setTemplates(refreshed.data || []);
    } catch (error) { toast.error(error.response?.data?.detail || "Veröffentlichung fehlgeschlagen"); }
  };

  const newVersion = () => setEditor({ id: null, title: published?.title || emptyTitles[activeTab], html: published?.html || "", sanitizedPreview: published?.html || "" });

  const saveTariffs = async () => {
    try {
      const payload = {
        single: { 6: centsInput(tariffs.single[6]), 12: centsInput(tariffs.single[12]) },
        community: { 6: centsInput(tariffs.community[6]), 12: centsInput(tariffs.community[12]) },
      };
      await contractSettingsApi.saveTariffs(payload);
      toast.success("Tarifvorlagen gespeichert");
    } catch (error) { toast.error(error.response?.data?.detail || error.message || "Tarife konnten nicht gespeichert werden"); }
  };

  if (loading) return <div className="flex min-h-[50vh] items-center justify-center gap-2 text-slate-600"><LoaderCircle className="h-5 w-5 animate-spin" /> Einstellungen werden geladen …</div>;

  return (
    <div className="mx-auto max-w-7xl space-y-6 animate-fade-in">
      <header><p className="text-sm font-semibold uppercase tracking-wider text-red-700">Administration</p><h1 className="mt-1 text-4xl font-bold">Vertrag &amp; Rechtstexte</h1><p className="mt-2 max-w-3xl text-slate-600">Tarife und angezeigte Rechtstexte werden hier zentral gespeichert. Veröffentlichte Fassungen sind unveränderlich.</p></header>
      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-3">{tabs.map((tab) => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`rounded-lg px-4 py-2 text-sm font-semibold ${activeTab === tab.id ? "bg-slate-950 text-white" : "text-slate-600 hover:bg-white"}`}>{tab.label}</button>)}</div>

      {activeTab === "tariffs" ? (
        <section className="space-y-5">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm leading-6 text-amber-900"><div className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" />Nur für zukünftige Vertragsentwürfe</div><p className="mt-1">Eine Änderung passt bestehende Verträge und bereits bezahlte Zeiträume nicht automatisch an. Individuelle Abteilungspreise überschreiben diese Vorlagen bewusst.</p></div>
          <div className="grid gap-5 lg:grid-cols-2">{[["single", "Einzeltarif"], ["community", "Gemeinschaftstarif"]].map(([code, label]) => <div key={code} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"><h2 className="text-xl font-semibold">{label}</h2><div className="mt-5 grid grid-cols-2 gap-4">{[6, 12].map((duration) => <label key={duration} className="text-sm font-medium text-slate-700">{duration} Monate · Gesamtpreis<input value={tariffs[code][duration]} onChange={(event) => setTariffs({ ...tariffs, [code]: { ...tariffs[code], [duration]: event.target.value } })} inputMode="decimal" className="input-admin" /><span className="mt-1 block text-xs text-slate-400">Euro, Speicherung als Cent</span></label>)}</div></div>)}</div>
          <button onClick={saveTariffs} className="rounded-xl bg-red-700 px-5 py-3 text-sm font-semibold text-white">Tarifvorlagen speichern</button>
        </section>
      ) : (
        <section className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"><div><div className="flex items-center gap-2"><FilePenLine className="h-4 w-4 text-red-700" /><span className="font-semibold">Aktuell veröffentlicht: {published ? `Version ${published.version}` : "noch keine Fassung"}</span></div><p className="mt-1 text-xs text-slate-500">{draft ? "Ein bearbeitbarer Entwurf ist vorhanden." : "Veröffentlichte Texte können nicht direkt geändert werden."}</p></div><div className="flex gap-2"><button onClick={newVersion} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold">Neue Fassung</button>{editor.id && <button onClick={publish} className="inline-flex items-center gap-2 rounded-lg bg-emerald-700 px-3 py-2 text-sm font-semibold text-white"><CheckCircle2 className="h-4 w-4" />Veröffentlichen</button>}</div></div>
          <LegalTextEditor title={editor.title} html={editor.html} onTitleChange={(title) => setEditor({ ...editor, title })} onHtmlChange={(html) => setEditor({ ...editor, html })} onSave={saveDraft} saving={saving} sanitizedPreview={editor.sanitizedPreview} />
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"><h2 className="font-semibold">Versionshistorie</h2><div className="mt-3 divide-y divide-slate-100">{versions.length ? versions.map((item) => <div key={item.id} className="flex items-center justify-between py-3 text-sm"><span>{item.title}</span><span className="text-slate-500">{item.status === "draft" ? "Entwurf" : `Version ${item.version} · ${item.status}`}</span></div>) : <p className="py-4 text-sm text-slate-500">Noch keine Fassung vorhanden.</p>}</div></div>
        </section>
      )}
    </div>
  );
}

